import React, { useState, useEffect } from 'react';
import { getMarketInsights, generateMarketInsights } from '../api';
import { useAuth } from '../context/AuthContext';
import MarketInsightsUpgradePlaceholder from '../components/MarketInsightsUpgradePlaceholder';
import MarketInsightsMarketDataPlaceholder from '../components/MarketInsightsMarketDataPlaceholder';
import LoadingSpinner from '../components/LoadingSpinner';
import { format } from 'date-fns';

// Utility function to parse markdown-style links and return React elements with actual links
const parseMarkdownLinks = (text: string): React.ReactElement => {
  if (!text) return <></>;

  // Regular expression to match Markdown-style links [text](url)
  const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  // Iterate over all matches of the regex in the text
  while ((match = regex.exec(text)) !== null) {
    const [fullMatch, linkText, url] = match;
    const matchStart = match.index;

    // Push the text before the match as a plain text span
    if (matchStart > lastIndex) {
      parts.push(
        <span key={key++}>
          {text.substring(lastIndex, matchStart)}
        </span>
      );
    }

    // Push the matched link as an anchor tag
    parts.push(
      <a
        key={key++}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#2196f3', textDecoration: 'underline' }}
      >
        {linkText}
      </a>
    );

    // Update the lastIndex to the end of the current match
    lastIndex = matchStart + fullMatch.length;
  }

  // Push any remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(
      <span key={key++}>
        {text.substring(lastIndex)}
      </span>
    );
  }

  return <>{parts}</>;
};

// Define keyframes for the spinner animation
const spinnerKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const MarketInsights: React.FC = () => {
  // Add the keyframes to the document head
  useEffect(() => {
    const style = document.createElement('style');
    style.type = 'text/css';
    style.appendChild(document.createTextNode(spinnerKeyframes));
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const { user } = useAuth();
  const [marketInsights, setMarketInsights] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [generating, setGenerating] = useState<boolean>(false);
  const [needsPlanUpgrade, setNeedsPlanUpgrade] = useState<boolean>(false);
  const [needsMarketData, setNeedsMarketData] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState<boolean>(window.innerWidth >= 768 && window.innerWidth < 1024);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchMarketInsights = async () => {
      if (!user?.customerId) return;

      try {
        setLoading(true);
        const data = await getMarketInsights(user.customerId);
        // data will be null if no insights are available, which is handled by the UI
        setMarketInsights(data?.insights || null);
        // Clear any previous errors
        setError('');
        setNeedsPlanUpgrade(false);
        setNeedsMarketData(false);
      } catch (err) {
        console.error('Error fetching market insights:', err);
        // Check if this is a plan upgrade error
        if (err instanceof Error && (err as any).type === 'PLAN_UPGRADE_REQUIRED') {
          setNeedsPlanUpgrade(true);
          setError('');
        } else if (err instanceof Error && (err as any).error === 'MISSING_MARKET_DATA') {
          setNeedsMarketData(true);
          setNeedsPlanUpgrade(false);
          setError('');
        } else if (err instanceof Error && err.message !== 'Customer ID is required') {
          setError('Failed to load market insights');
          setNeedsPlanUpgrade(false);
          setNeedsMarketData(false);
        }
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchMarketInsights();
    }
  }, [user?.customerId]);

  const handleGenerateInsights = async () => {
    if (!user?.customerId) return;

    try {
      setGenerating(true);
      setError('');

      // Call the API to generate insights (now returns the insights directly)
      console.log('Starting generation of market insights...');
      const response = await generateMarketInsights(user.customerId);
      console.log('Generation response:', response);

      // Check if we have insights in the response
      if (!response || !response.insights) {
        console.error('No insights data returned:', response);
        throw new Error('Failed to generate market insights');
      }

      // Set the insights directly from the response
      console.log('Setting market insights:', response.insights);
      setMarketInsights(response.insights);
      setNeedsPlanUpgrade(false);
      setNeedsMarketData(false);

    } catch (err) {
      console.error('Error generating market insights:', err);
      // Check if this is a plan upgrade error
      if (err instanceof Error && (err as any).type === 'PLAN_UPGRADE_REQUIRED') {
        setNeedsPlanUpgrade(true);
        setError('');
      } else if (err instanceof Error && (err as any).error === 'MISSING_MARKET_DATA') {
        setNeedsMarketData(true);
        setNeedsPlanUpgrade(false);
        setError('');
      } else if (err instanceof Error) {
        setError(err.message);
        setNeedsPlanUpgrade(false);
        setNeedsMarketData(false);
      } else {
        setError('Failed to generate market insights');
        setNeedsPlanUpgrade(false);
        setNeedsMarketData(false);
      }
    } finally {
      setGenerating(false);
    }
  };

  // If we're still loading, show a loading indicator
  if (loading) {
    return <LoadingSpinner title="Loading market insights..." />;
  }

  // If no data is available yet or there was an error, show a message with a generate button
  if (!marketInsights) {
    return (
      <div style={{
        padding: isMobile ? '1rem' : '2rem',
        margin: isMobile ? '20px auto' : '40px auto'
      }}>
        <div style={{
          maxWidth: isMobile ? '95%' : isTablet ? '90%' : '80%',
          margin: '0 auto',
          padding: isMobile ? '1rem' : '2rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          {needsPlanUpgrade ? (
            <MarketInsightsUpgradePlaceholder
              onUpgrade={() => {
                window.location.href = '/settings?tab=accounts-billing';
              }}
            />
          ) : needsMarketData ? (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <MarketInsightsMarketDataPlaceholder
                onConfigure={() => {
                  window.location.href = '/settings?tab=market-data';
                }}
              />
            </div>
          ) : (
            <>
              {error && (
                <div style={{
                  padding: isMobile ? '10px 12px' : '12px 16px',
                  marginBottom: isMobile ? '16px' : '20px',
                  background: 'rgba(244,67,54,0.1)',
                  borderRadius: '4px',
                  color: '#e53935',
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '6px' : '8px'
                }}>
                  <svg width={isMobile ? "18" : "20"} height={isMobile ? "18" : "20"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C16.418 20 20 16.418 20 12C20 7.582 16.418 4 12 4C7.582 4 4 7.582 4 12C4 16.418 7.582 20 12 20ZM11 15H13V17H11V15ZM11 7H13V13H11V7Z" fill="#e53935" />
                  </svg>
                  <span style={{
                    fontSize: isMobile ? '14px' : 'inherit',
                    lineHeight: isMobile ? '1.4' : 'inherit'
                  }}>Unable to load existing market insights. You can try generating new insights below.</span>
                </div>
              )}
            </>
          )}

          {!needsPlanUpgrade && !needsMarketData && (
            <>
              <h3 style={{
                fontSize: isMobile ? 20 : 24,
                textAlign: 'center',
                margin: isMobile ? '0 0 12px 0' : '0 0 16px 0'
              }}>{error ? 'Generate New Market Insights' : 'No Market Insights Available'}</h3>
              <p style={{
                fontSize: isMobile ? 14 : 16,
                textAlign: 'center',
                lineHeight: isMobile ? '1.4' : '1.5',
                margin: isMobile ? '0 0 16px 0' : '0 0 20px 0'
              }}>Generate insights to see AI-powered analysis of market trends and external factors affecting your business.</p>

              <button
                onClick={handleGenerateInsights}
                disabled={generating}
                style={{
                  background: 'linear-gradient(90deg,#1565c0 60%,#1976d2 100%)',
                  color: '#fff',
                  padding: isMobile ? '12px 16px' : '10px 20px',
                  borderRadius: '4px',
                  border: 'none',
                  cursor: generating ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  marginTop: isMobile ? '12px' : '16px',
                  fontSize: isMobile ? '14px' : '16px',
                  minHeight: isMobile ? '44px' : 'auto'
                }}
              >
                {generating ? 'Generating...' : 'Generate Market Insights'}
              </button>
            </>
          )}

          {generating && (
            <div id="generation-progress" style={{
              marginTop: isMobile ? '12px' : '16px',
              padding: isMobile ? '12px' : '16px',
              background: 'rgba(21, 101, 192, 0.1)',
              borderRadius: '4px',
              color: '#1565c0',
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '8px' : '12px',
              flexDirection: isMobile ? 'column' : 'row',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              <div style={{
                width: isMobile ? '20px' : '24px',
                height: isMobile ? '20px' : '24px',
                border: isMobile ? '2px solid rgba(21, 101, 192, 0.3)' : '3px solid rgba(21, 101, 192, 0.3)',
                borderTop: isMobile ? '2px solid #1565c0' : '3px solid #1565c0',
                borderRadius: '50%',
                WebkitAnimation: 'spin 1s linear infinite',
                animation: 'spin 1s linear infinite',
                WebkitTransform: 'translateZ(0)',
                msTransform: 'translateZ(0)',
                transform: 'translateZ(0)',
                alignSelf: isMobile ? 'center' : 'auto'
              }} />
              <div style={{
                fontSize: isMobile ? '14px' : '16px',
                lineHeight: isMobile ? '1.4' : '1.5'
              }}>Generating market insights... This may take a minute. If you navigate away, the generation will continue in the background.</div>
            </div>
          )}

        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: isMobile ? '95%' : isTablet ? '90%' : '80%',
      margin: isMobile ? '20px auto' : '40px auto',
      padding: isMobile ? '0 0.5rem' : '0'
    }}>

      {/* Market Insights - External Factors */}
      <div style={{
        background: 'linear-gradient(90deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
        color: '#fff',
        borderRadius: isMobile ? 12 : 16,
        padding: isMobile ? '1.5rem' : isTablet ? '1.75rem' : '2rem',
        marginBottom: isMobile ? 24 : 32,
        boxShadow: '0 4px 24px rgba(21,101,192,0.08)'
      }}>
        <h2 style={{
          margin: 0,
          fontSize: isMobile ? 24 : isTablet ? 26 : 28,
          lineHeight: isMobile ? '1.2' : '1.3'
        }}>Market & Industry Insights</h2>
        <p style={{
          fontSize: isMobile ? 16 : isTablet ? 17 : 18,
          margin: isMobile ? '0.75rem 0 1rem' : '1rem 0 1.5rem',
          lineHeight: isMobile ? '1.4' : '1.5'
        }}>{parseMarkdownLinks(marketInsights.summary)}</p>

        <div style={{ marginBottom: isMobile ? '1rem' : '1.5rem' }}>
          <h3 style={{
            fontSize: isMobile ? 18 : 20,
            marginBottom: isMobile ? '0.5rem' : '0.75rem'
          }}>Key External Factors</h3>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: isMobile ? 12 : 16,
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            {marketInsights.factors && marketInsights.factors.map((factor: any, index: number) => (
              <div key={index} style={{
                flex: isMobile ? 'none' : '1 1 45%',
                minWidth: isMobile ? 'auto' : 280,
                background: 'rgba(255,255,255,0.15)',
                padding: isMobile ? 12 : 16,
                borderRadius: isMobile ? 6 : 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  marginBottom: isMobile ? 6 : 8,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '4px' : '0'
                }}>
                  <h4 style={{
                    margin: 0,
                    fontSize: isMobile ? 16 : 'inherit',
                    lineHeight: isMobile ? '1.3' : 'inherit'
                  }}>{factor.name}</h4>
                  <span style={{
                    padding: isMobile ? '2px 6px' : '2px 8px',
                    borderRadius: 12,
                    fontSize: isMobile ? 11 : 12,
                    fontWeight: 'bold',
                    background: factor.impact === 'High' ? 'rgba(244,67,54,0.9)' :
                      factor.impact === 'Medium' ? 'rgba(255,152,0,0.9)' : 'rgba(76,175,80,0.9)',
                    alignSelf: isMobile ? 'flex-start' : 'auto'
                  }}>
                    {factor.impact}
                  </span>
                </div>
                <p style={{
                  margin: 0,
                  fontSize: isMobile ? 13 : 14,
                  lineHeight: isMobile ? '1.4' : '1.5'
                }}>{parseMarkdownLinks(factor.description)}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: isMobile ? 16 : 24,
          flexWrap: 'wrap',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <div style={{
            flex: 1,
            minWidth: isMobile ? 'auto' : 280
          }}>
            <h3 style={{
              fontSize: isMobile ? 18 : 20,
              marginBottom: isMobile ? '0.5rem' : '0.75rem'
            }}>Opportunities</h3>
            <ul style={{
              margin: 0,
              paddingLeft: isMobile ? 16 : 20
            }}>
              {marketInsights.opportunities && marketInsights.opportunities.map((item: string, index: number) => (
                <li key={index} style={{
                  marginBottom: isMobile ? 6 : 8,
                  fontSize: isMobile ? 14 : 'inherit',
                  lineHeight: isMobile ? '1.4' : '1.5'
                }}>{parseMarkdownLinks(item)}</li>
              ))}
            </ul>
          </div>
          <div style={{
            flex: 1,
            minWidth: isMobile ? 'auto' : 280
          }}>
            <h3 style={{
              fontSize: isMobile ? 18 : 20,
              marginBottom: isMobile ? '0.5rem' : '0.75rem'
            }}>Threats</h3>
            <ul style={{
              margin: 0,
              paddingLeft: isMobile ? 16 : 20
            }}>
              {marketInsights.threats && marketInsights.threats.map((item: string, index: number) => (
                <li key={index} style={{
                  marginBottom: isMobile ? 6 : 8,
                  fontSize: isMobile ? 14 : 'inherit',
                  lineHeight: isMobile ? '1.4' : '1.5'
                }}>{parseMarkdownLinks(item)}</li>
              ))}
            </ul>
          </div>
        </div>

        <div style={{
          marginTop: isMobile ? 12 : 16,
          display: 'flex',
          justifyContent: 'left',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '8px' : '0'
        }}>
          <div style={{
            fontSize: isMobile ? 13 : 14,
            opacity: 0.8
          }}>
            Last updated: {marketInsights.lastUpdated ? format(new Date(marketInsights.lastUpdated), 'do MMMM yyyy') : 'N/A'}
          </div>
          {marketInsights.lastUpdated &&
            (new Date().getTime() - new Date(marketInsights.lastUpdated).getTime() > 1 * 24 * 60 * 60 * 1000) && (
              <button
                onClick={handleGenerateInsights}
                disabled={generating}
                style={{
                  background: generating ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: isMobile ? '4px' : '6px',
                  padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem',
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  fontWeight: '500',
                  cursor: generating ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '6px' : '8px',
                  marginLeft: isMobile ? 0 : 16,
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(10px)',
                  alignSelf: isMobile ? 'flex-start' : 'auto'
                }}
                onMouseEnter={(e) => {
                  if (!generating) {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!generating) {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                  }
                }}
              >
                {generating ? (
                  <>
                    <div style={{
                      width: isMobile ? '14px' : '16px',
                      height: isMobile ? '14px' : '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid #ffffff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    {isMobile ? 'Updating...' : 'Updating...'}
                  </>
                ) : (
                  <>{isMobile ? 'Refresh' : 'Refresh Insights'}</>
                )}
              </button>
            )}
        </div>
      </div>
    </div>
  );
};

export default MarketInsights;
